/**
 * Opt-in performance capture overlay (issue #17). A low-power laptop where a
 * stutter reproduces cannot run DevTools-heavy profiling, so this lets a
 * plain browser CAPTURE the numbers — live FPS/frame time/draw calls/scene
 * memory counts, boot-phase timings, and device/GPU info — and download them
 * as a JSON report; analysis and fixes happen off-device. Purely diagnostic:
 * it never reads from or feeds into any LOS code, and atmosphere/fade/quality
 * cosmetics are untouched by its presence (hard constraint: cosmetic tooling
 * must never influence any LOS result or rendering behaviour).
 *
 * Gated end-to-end behind `?perf=1` via createPerfOverlay(): when the flag is
 * absent it returns `undefined` and constructs nothing at all — no DOM, no
 * recorder, no BootTimer, no per-frame allocation. main.ts calls every
 * overlay method through optional chaining (`perfOverlay?.sample(...)`), so
 * the default (no-perf) path costs a single undefined check per call site,
 * never a method invocation.
 *
 * The pure pieces (BootTimer, PerfRecorder, buildPerfReport,
 * sampleFromRendererInfo, parsePerfFlag, formatSample) are three.js- and
 * DOM-free and unit-tested directly in Node, mirroring the RouteRibbon /
 * BuildingsLayer split of pure geometry vs. a thin DOM/three wrapper. Only
 * PerfOverlay itself touches the DOM.
 */

/** Reads `?perf=1` from a location search string; any other value (or its
 * absence) is off. */
export function parsePerfFlag(search: string): boolean {
  return new URLSearchParams(search).get('perf') === '1';
}

/** Duck-typed subset of three's WebGLRenderer.info — kept structural (no
 * `import type { WebGLRenderer } from 'three'`) so the pure sampling logic
 * has no three.js dependency and can be unit-tested with a plain object. */
export interface RendererInfoLike {
  render: { calls: number; triangles: number };
  memory: { geometries: number; textures: number };
}

/** RendererInfoLike plus the reset controls the real renderer.info carries.
 * PerfOverlay.sample needs these because the app renders through an
 * EffectComposer: one frame is several renderer.render() calls (scene pass,
 * bloom's internal blur quads, grade, OutputPass) and three.js resets the
 * counters at the start of every one, so renderer.info read at a frame
 * boundary reflects only the LAST pass. Measured on the production build
 * (issue #17 post-batch review): the overlay showed "draw calls 1,
 * triangles 1" while the true frame was ~1,665 calls / ~1.4M triangles.
 * Whole-frame capture turns autoReset off and resets manually after each
 * sample, so each sample covers exactly the renders since the previous one. */
export interface ResettableRendererInfo extends RendererInfoLike {
  autoReset: boolean;
  reset(): void;
}

export interface PerfSample {
  /** ms since the capturing BootTimer/clock started. */
  atMs: number;
  fps: number;
  frameTimeMs: number;
  drawCalls: number;
  triangles: number;
  /** three.js does not track GPU bytes, only live object counts — reported
   * as a memory-pressure proxy, not bytes. */
  geometries: number;
  textures: number;
}

/** Builds one sample from a renderer.info snapshot and the frame's delta time. */
export function sampleFromRendererInfo(info: RendererInfoLike, frameTimeMs: number, atMs: number): PerfSample {
  return {
    atMs,
    fps: frameTimeMs > 0 ? 1000 / frameTimeMs : 0,
    frameTimeMs,
    drawCalls: info.render.calls,
    triangles: info.render.triangles,
    geometries: info.memory.geometries,
    textures: info.memory.textures,
  };
}

/** Formats a sample for the on-screen overlay panel. */
export function formatSample(s: PerfSample): string {
  return [
    `fps ${s.fps.toFixed(0)}  frame ${s.frameTimeMs.toFixed(1)} ms`,
    `draw calls ${s.drawCalls}  triangles ${s.triangles.toLocaleString('en-GB')}`,
    `geometries ${s.geometries}  textures ${s.textures}`,
  ].join('\n');
}

export interface BootMark {
  name: string;
  /** ms since the BootTimer was constructed. */
  atMs: number;
  /** ms since the previous mark (or since construction, for the first mark). */
  sinceLastMs: number;
}

/** Records named boot-phase markers relative to construction time. Pure and
 * clock-injectable so it's unit-tested without a real `performance` global. */
export class BootTimer {
  private readonly now: () => number;
  private readonly start: number;
  private readonly marks: BootMark[] = [];
  private readonly seen = new Set<string>();

  constructor(now: () => number = () => performance.now()) {
    this.now = now;
    this.start = this.now();
  }

  mark(name: string): void {
    const atMs = this.now() - this.start;
    const prev = this.marks.length > 0 ? this.marks[this.marks.length - 1].atMs : 0;
    this.marks.push({ name, atMs, sinceLastMs: atMs - prev });
  }

  /** Records `name` only on its first call — guards one-shot boot markers
   * (e.g. "first-frame") that would otherwise be called every render tick. */
  markOnce(name: string): void {
    if (this.seen.has(name)) return;
    this.seen.add(name);
    this.mark(name);
  }

  timings(): BootMark[] {
    return [...this.marks];
  }
}

export interface PerfSummary {
  sampleCount: number;
  avgFps: number;
  minFps: number;
  maxFps: number;
  avgDrawCalls: number;
  avgTriangles: number;
}

function average(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function summarise(samples: PerfSample[]): PerfSummary {
  if (samples.length === 0) {
    return { sampleCount: 0, avgFps: 0, minFps: 0, maxFps: 0, avgDrawCalls: 0, avgTriangles: 0 };
  }
  const fpsValues = samples.map((s) => s.fps);
  return {
    sampleCount: samples.length,
    avgFps: average(fpsValues),
    minFps: Math.min(...fpsValues),
    maxFps: Math.max(...fpsValues),
    avgDrawCalls: average(samples.map((s) => s.drawCalls)),
    avgTriangles: average(samples.map((s) => s.triangles)),
  };
}

/** Rolling window of live samples for a capture session (a laptop user is
 * expected to exercise boot + a ~30s route fly-through, per the issue #17
 * workflow), bounded so an unattended long-running session cannot grow
 * without limit. */
const DEFAULT_MAX_SAMPLES = 3600; // ~60s at 60fps

export class PerfRecorder {
  private readonly samples: PerfSample[] = [];
  private readonly maxSamples: number;

  constructor(maxSamples: number = DEFAULT_MAX_SAMPLES) {
    this.maxSamples = maxSamples;
  }

  record(sample: PerfSample): void {
    this.samples.push(sample);
    if (this.samples.length > this.maxSamples) this.samples.shift();
  }

  latest(): PerfSample | undefined {
    return this.samples[this.samples.length - 1];
  }

  all(): PerfSample[] {
    return [...this.samples];
  }

  summary(): PerfSummary {
    return summarise(this.samples);
  }
}

export interface DeviceInfo {
  /** Unmasked GPU renderer string (WEBGL_debug_renderer_info), if available. */
  gpuRenderer: string | undefined;
  devicePixelRatio: number;
  hardwareConcurrency: number | undefined;
  qualityTier: string;
  userAgent: string;
}

export interface PerfReport {
  generatedAt: string;
  device: DeviceInfo;
  bootTimings: BootMark[];
  summary: PerfSummary;
  samples: PerfSample[];
}

/** Assembles a full downloadable report from a device snapshot, boot
 * timings, and the samples captured during the session. */
export function buildPerfReport(params: {
  device: DeviceInfo;
  bootTimings: BootMark[];
  samples: PerfSample[];
  generatedAt?: string;
}): PerfReport {
  return {
    generatedAt: params.generatedAt ?? new Date().toISOString(),
    device: params.device,
    bootTimings: params.bootTimings,
    summary: summarise(params.samples),
    samples: params.samples,
  };
}

export interface PerfOverlayDeps {
  /** Read live at report-build time (not cached) since the quality tier can
   * change mid-session via the quality toggle. */
  getDeviceInfo: () => DeviceInfo;
}

/** The DOM-touching half: a small fixed panel plus a download button. Only
 * ever constructed when the perf flag is on — see createPerfOverlay(). */
export class PerfOverlay {
  private readonly deps: PerfOverlayDeps;
  private readonly recorder = new PerfRecorder();
  private readonly bootTimer: BootTimer;
  private readonly panel: HTMLElement;
  private readonly metricsEl: HTMLElement;
  /** Whole-frame capture armed (see {@link ResettableRendererInfo}): the
   * first sample() call only disables autoReset and zeroes the counters —
   * the values it sees are last-pass-only junk and are never recorded. */
  private armed = false;

  constructor(deps: PerfOverlayDeps, now?: () => number) {
    this.deps = deps;
    this.bootTimer = new BootTimer(now);

    this.panel = document.createElement('div');
    this.panel.id = 'perf-overlay';
    // Inline-styled (not style.css) so this debug-only panel is fully
    // self-contained: position: fixed takes it out of #app's normal flow,
    // which is otherwise `overflow: hidden` at 100% height and would
    // silently clip an unpositioned block appended after it.
    this.panel.style.cssText =
      // top:60px clears #panel-nav (fixed, top:12px) so the overlay never
      // overlaps the nav buttons/viewpoint dropdown it shares the corner with.
      'position:fixed;top:60px;right:8px;z-index:9999;pointer-events:auto;' +
      'background:rgba(10,14,12,0.85);color:#eef2ee;font:12px/1.4 ui-monospace,monospace;' +
      'padding:8px 10px;border-radius:6px;white-space:pre;';

    this.metricsEl = document.createElement('pre');
    this.metricsEl.id = 'perf-overlay-metrics';
    this.metricsEl.style.cssText = 'margin:0 0 6px 0;';
    this.metricsEl.textContent = 'perf: waiting for first frame…';
    this.panel.appendChild(this.metricsEl);

    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'perf-overlay-download';
    button.textContent = 'Download perf report';
    button.addEventListener('click', () => this.triggerDownload());
    this.panel.appendChild(button);

    document.body.appendChild(this.panel);
  }

  mark(name: string): void {
    this.bootTimer.mark(name);
  }

  markOnce(name: string): void {
    this.bootTimer.markOnce(name);
  }

  /**
   * Records one whole-frame sample. Re-asserts `autoReset = false` on every
   * call (a hostile or unaware re-enable would silently drop back to
   * last-pass-only numbers), and resets the counters after reading so the
   * next call covers exactly one frame. The very first call only arms this
   * capture mode and records nothing: the counters at that point still hold
   * whatever the last composer pass left behind, not a whole frame.
   *
   * Only ever constructed behind `?perf=1`, so flipping the renderer's
   * info bookkeeping here never affects a normal session.
   */
  sample(info: ResettableRendererInfo, frameTimeMs: number, atMs: number): void {
    info.autoReset = false;
    if (!this.armed) {
      this.armed = true;
      info.reset();
      this.metricsEl.textContent = 'perf: capturing first whole frame…';
      return;
    }
    const s = sampleFromRendererInfo(info, frameTimeMs, atMs);
    info.reset();
    this.recorder.record(s);
    this.metricsEl.textContent = formatSample(s);
  }

  /** Assembles the current report — exposed directly so report content can
   * be unit-tested without exercising the browser download mechanics. */
  buildReport(): PerfReport {
    return buildPerfReport({
      device: this.deps.getDeviceInfo(),
      bootTimings: this.bootTimer.timings(),
      samples: this.recorder.all(),
    });
  }

  private triggerDownload(): void {
    const report = this.buildReport();
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ray2-perf-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Removes the panel from the DOM (test cleanup / hot-reload hygiene). */
  destroy(): void {
    this.panel.remove();
  }
}

/**
 * Sole entry point main.ts should use. Returns `undefined` (constructing
 * nothing) unless `search` carries `?perf=1`, which is what makes the
 * overlay zero-cost by default: callers invoke every method through optional
 * chaining (`perfOverlay?.sample(...)`), so the disabled path never
 * allocates a recorder, a BootTimer, or a DOM node.
 */
export function createPerfOverlay(search: string, deps: PerfOverlayDeps, now?: () => number): PerfOverlay | undefined {
  if (!parsePerfFlag(search)) return undefined;
  return new PerfOverlay(deps, now);
}
